import { persistantStore } from "../../../../store/persistantStore";
import { unifiedDataStore } from "../../../../store/unifiedDataStore";
import {
  selectedEvents,
  selectedGroupKeys,
  setHasSelection,
  setLegendSelectionActive,
  setSelectedEvents,
  setSelectedGroupKeys,
  setTriggerSelection,
  toggleEventIds
} from "../../../../store/selectionStore";
import {
  color,
  eventType as currentEventType,
  grouped,
  maneuvers,
  setEventType,
  tabledata
} from "../../../../store/globalStore";
import { toastStore } from "../../../../store/toastStore";
import {
  MANEUVER_MARKER_EVENT_TYPES,
  maneuverLetterToEventType,
  normalizeManeuverEventType
} from "../../../../utils/maneuverEventType";
import { getManeuverGroupKeyFromFirstPoint, groupKeyEquals } from "../../../../utils/colorGrouping";
import { clearManeuverSelectionOnEventTypeChange } from "../../../../utils/maneuverEventTypeSelection";
import { scrollToEvidence } from "../../../../utils/scrollToEvidence";
import { debug, warn } from "../../../../utils/console";
import { noteMapManeuverClick } from "../../../../utils/mapManeuverClick";
import { selectAfterManeuverMapRecenter } from "../../../../utils/maneuverMapRecenter";

/**
 * How far a clicked point may sit from an event's window before we treat it as "no maneuver here".
 * Map rows carry no event_id (Map.py writes neither the dataset nor the day object with one), so every
 * click resolves by timestamp; without a bound the nearest event always won and a click could select a
 * maneuver minutes away.
 */
const MATCH_TOLERANCE_MS = 60_000;

/** Row from fetchEvents / HuniDB (avoid name `Event` — conflicts with DOM lib). */
type ManeuverEventRow = {
  event_id?: number;
  event_type?: string;
  start_time?: string;
  end_time?: string;
};

/** The maneuver a click resolved to. */
export interface ResolvedManeuverEvent {
  event_id: number;
  event_type: string | null;
}

const MANEUVER_PAGE_SELECTORS = [
  "[data-fleet-maneuvers-page]",
  "[data-fleet-maneuvers-history-page]",
  "[data-dataset-maneuvers-page]",
  "[data-maneuvers-history-page]",
  "[data-live-maneuvers-page]",
] as const;

/** Retries cover a type-switch refetch before the row exists in the split panel. */
const REVEAL_DELAYS_MS = [0, 50, 150, 400, 800, 1500, 2500];

/**
 * True when a maneuver report is one of the split-view panels.
 *
 * The map and the maneuver page then share this window. A marker click has to land in that page's
 * selection, not only on the map track.
 */
export function isManeuverPageInSplitView(): boolean {
  if (typeof document === "undefined") return false;
  const split = document.querySelector(".split-view-content");
  if (!split) return false;
  return MANEUVER_PAGE_SELECTORS.some((selector) => split.querySelector(selector) != null);
}

function eachLoadedManeuverRow(): Record<string, unknown>[] {
  const rows: Record<string, unknown>[] = [];
  const pools = [maneuvers(), tabledata()];
  for (const pool of pools) {
    if (!Array.isArray(pool)) continue;
    for (const row of pool) {
      if (row != null && typeof row === "object") rows.push(row as Record<string, unknown>);
    }
  }
  return rows;
}

function findLoadedManeuverRow(eventId: number): Record<string, unknown> | null {
  for (const row of eachLoadedManeuverRow()) {
    const id = Number(row.event_id);
    if (id === eventId) return row;
  }
  return null;
}

function loadedRowWindow(row: Record<string, unknown>): { start: number; end: number } | null {
  const startRaw = row.start_time ?? row.Start_time ?? row.datetime ?? row.Datetime ?? row.date ?? row.Date;
  if (startRaw == null) return null;
  const start = new Date(startRaw as string | number | Date).getTime();
  if (!Number.isFinite(start)) return null;
  const endRaw = row.end_time ?? row.End_time ?? startRaw;
  const end = new Date(endRaw as string | number | Date).getTime();
  return { start, end: Number.isFinite(end) ? end : start };
}

/**
 * The id the open maneuvers page can actually highlight.
 *
 * A map point's own event_id is not always that id. Selecting a foreign one sets a selection the
 * page cannot build a time range for, so the track goes grey and every maneuver row fades.
 */
function matchLoadedManeuver(
  eventId: number | null,
  timeMs: number | null,
  clickedType: string | null
): { event_id: number; event_type: string | null } | null {
  const rows = eachLoadedManeuverRow();
  if (rows.length === 0) return null;

  if (eventId != null) {
    const byId = rows.find((row) => Number(row.event_id) === eventId);
    if (byId) {
      const type = String(byId.event_type ?? clickedType ?? "").toUpperCase() || clickedType;
      return { event_id: eventId, event_type: type };
    }
  }

  if (timeMs == null || !Number.isFinite(timeMs)) return null;
  const pool = clickedType
    ? rows.filter((row) => String(row.event_type ?? "").toUpperCase() === clickedType)
    : rows;
  if (pool.length === 0) return null;

  let matching: Record<string, unknown> | null = null;
  let closest: Record<string, unknown> | null = null;
  let closestDistance = Infinity;
  for (const row of pool) {
    const window = loadedRowWindow(row);
    if (!window) continue;
    if (timeMs >= window.start && timeMs <= window.end) {
      matching = row;
      break;
    }
    const minDist = Math.min(Math.abs(timeMs - window.start), Math.abs(timeMs - window.end));
    if (minDist < closestDistance) {
      closestDistance = minDist;
      closest = row;
    }
  }

  const chosen = matching ?? (closestDistance <= MATCH_TOLERANCE_MS ? closest : null);
  const id = chosen ? Number(chosen.event_id) : NaN;
  if (chosen == null || !Number.isFinite(id) || id <= 0) return null;
  const type = String(chosen.event_type ?? clickedType ?? "").toUpperCase() || clickedType;
  return { event_id: id, event_type: type };
}

/**
 * Put `eventId` in the maneuver selection without toggling it off.
 *
 * A second click on the map must not drop the maneuver the split page is showing. Deselect stays
 * on the table row.
 */
function ensureEventSelected(eventId: number): void {
  const current = selectedEvents();
  if (!current.includes(eventId)) {
    setLegendSelectionActive(false);
    setSelectedEvents([...current, eventId], { defer: false });
  }
  setHasSelection(true);
  setTriggerSelection(true);
}

/** Grouped maneuver pages highlight group keys, not event ids. Add the clicked row's group. */
function ensureGroupedSelection(eventId: number, attempt = 0): void {
  if (!grouped()) return;
  const row = findLoadedManeuverRow(eventId);
  if (!row) {
    if (attempt + 1 < REVEAL_DELAYS_MS.length && typeof window !== "undefined") {
      const wait = REVEAL_DELAYS_MS[attempt + 1] - REVEAL_DELAYS_MS[attempt];
      window.setTimeout(() => ensureGroupedSelection(eventId, attempt + 1), wait);
    }
    return;
  }
  const key = getManeuverGroupKeyFromFirstPoint(row, color());
  if (key === "" || key == null) return;
  const current = selectedGroupKeys();
  if (!current.some((existing) => groupKeyEquals(existing, key))) {
    setSelectedGroupKeys([...current, key]);
  }
  setHasSelection(true);
  setTriggerSelection(true);
}

/** Scroll the split-view table row into its panel once the row has rendered. */
function revealManeuverRow(eventId: number, attempt = 0): void {
  if (typeof document === "undefined") return;
  const selector = `.split-view-content tr[data-maneuver-event-id="${eventId}"]`;
  const row = document.querySelector(selector);
  if (row instanceof HTMLElement) {
    scrollToEvidence(selector);
    return;
  }
  if (attempt + 1 < REVEAL_DELAYS_MS.length) {
    const wait = REVEAL_DELAYS_MS[attempt + 1] - REVEAL_DELAYS_MS[attempt];
    window.setTimeout(() => revealManeuverRow(eventId, attempt + 1), wait);
  }
}

function selectForSplitManeuverPage(eventId: number): void {
  ensureEventSelected(eventId);
  ensureGroupedSelection(eventId);
  revealManeuverRow(eventId);
}

/**
 * Select the event id.
 *
 * Clicking a maneuver of a different kind than the maneuver pages are showing switches them to
 * that kind, otherwise the clicked maneuver is selected but nowhere on screen. That switch follows
 * the same rule as the maneuver dropdowns — a different type is a different set of events, so the
 * old selection is dropped. When the maneuver map is open, the new type is drawn and fitted in
 * full before the clicked maneuver is selected. Selecting first frames the old type, or only the
 * one clicked event. A click within the type already on screen just toggles, so several
 * maneuvers can still be compared.
 *
 * When a maneuver page is open in split view the click must leave that maneuver selected there
 * (and scroll its row into view). Toggling it off, or losing it to the map brush reset that
 * follows a type change, is the opposite of what the click is for.
 */
function selectResolvedEvent(eventId: number, eventType: string | null): ResolvedManeuverEvent {
  const nextType = eventType ? normalizeManeuverEventType(eventType) : null;
  const typeChanged = nextType != null && nextType !== normalizeManeuverEventType(currentEventType());
  const splitManeuverPage = isManeuverPageInSplitView();

  if (typeChanged) {
    setEventType(nextType);
    clearManeuverSelectionOnEventTypeChange('MapManeuverClick');
    const select = () => {
      if (splitManeuverPage) selectForSplitManeuverPage(eventId);
      else toggleEventIds([eventId]);
      noteMapManeuverClick(eventId, nextType);
    };
    // The maneuver map has to load the new type and fit every track before this click is
    // selected. Selecting now would frame the previous type, or just this one event.
    const maneuverMapOpen = typeof document !== "undefined" && document.querySelector(".maneuver-map") != null;
    if (maneuverMapOpen) {
      selectAfterManeuverMapRecenter(select);
    } else {
      // clearSelection({ immediate: true }) writes selectedEvents synchronously and only releases
      // setSelectedEvents' recursion guard on a microtask — selecting in this tick would be dropped
      // by that guard. A macrotask lands after the release.
      setTimeout(select, 0);
    }
    return { event_id: eventId, event_type: nextType };
  }

  if (splitManeuverPage) selectForSplitManeuverPage(eventId);
  else toggleEventIds([eventId]);
  noteMapManeuverClick(eventId, nextType);
  return { event_id: eventId, event_type: nextType };
}

/**
 * Resolve a map track point with Maneuver_type to global selectedEvents (fleet day points carry dataset_id).
 *
 * Returns the resolved event, or null when the click could not be tied to one.
 */
export async function applyManeuverPointToSelection(
  d: Record<string, unknown>,
  logPrefix: string
): Promise<ResolvedManeuverEvent | null> {
  // The marker's own letter (T/G/R/B) says which kind of maneuver was clicked.
  const clickedType = maneuverLetterToEventType(d.Maneuver_type as string | undefined);

  const rawEventId = d.event_id ?? d.Event_id;
  const parsedEventId = Number(rawEventId);
  const pointEventId = Number.isFinite(parsedEventId) && parsedEventId > 0 ? parsedEventId : null;
  const splitPage = isManeuverPageInSplitView();
  const clickTimeRaw = d.Datetime ?? d.timestamp ?? d.time ?? d.datetime;
  const clickTimeMs = clickTimeRaw == null ? null : new Date(clickTimeRaw as string | number | Date).getTime();

  if (splitPage) {
    const fromPage = matchLoadedManeuver(
      pointEventId,
      Number.isFinite(clickTimeMs) ? clickTimeMs : null,
      clickedType
    );
    if (fromPage) {
      debug(`${logPrefix}: Maneuver matched the open maneuvers page`, fromPage);
      return selectResolvedEvent(fromPage.event_id, fromPage.event_type);
    }
  }

  // With the page's own rows loaded, a point id that is not one of them must not be selected:
  // the page cannot draw a range for it, and the map greys the whole track.
  const pageHasRows = splitPage && eachLoadedManeuverRow().length > 0;
  if (pointEventId != null && !pageHasRows) {
    debug(`${logPrefix}: Maneuver used point event_id`, { event_id: pointEventId });
    return selectResolvedEvent(pointEventId, clickedType);
  }

  const className = persistantStore.selectedClassName?.();
  const projectId = persistantStore.selectedProjectId?.();
  if (!className || projectId == null) {
    warn(`${logPrefix}: Missing className or projectId for maneuver→event lookup`);
    return null;
  }

  const fromPoint = Number(d.dataset_id ?? d.datasetId);
  const datasetId =
    Number.isFinite(fromPoint) && fromPoint > 0
      ? fromPoint
      : persistantStore.selectedDatasetId?.() ?? 0;

  if (!datasetId || !Number.isFinite(datasetId)) {
    warn(`${logPrefix}: No dataset_id on point and none in store; cannot load events`, {
      fromPoint,
      storeDatasetId: persistantStore.selectedDatasetId?.()
    });
    toastStore.showToast('warning', 'Could not select maneuver', 'No dataset context for this point.');
    return null;
  }

  const datetime = d.Datetime ?? d.timestamp ?? d.time ?? d.datetime;
  if (datetime == null) {
    warn(`${logPrefix}: Maneuver point missing time field`);
    return null;
  }

  try {
    const allEvents = (await unifiedDataStore.fetchEvents(className, projectId, datasetId)) as
      | ManeuverEventRow[]
      | undefined;

    if (!allEvents || allEvents.length === 0) {
      warn(`${logPrefix}: No events loaded for dataset`, { datasetId });
      toastStore.showToast('warning', 'Could not select maneuver', 'No maneuver events are loaded for this dataset.');
      return null;
    }

    const maneuverTime = new Date(datetime as string | number | Date).getTime();
    if (!Number.isFinite(maneuverTime)) {
      warn(`${logPrefix}: Invalid maneuver time`, { datetime });
      return null;
    }

    // The marker's letter is authoritative: a takeoff must never resolve to a neighbouring tack, so a
    // known letter restricts the candidates outright rather than merely preferring them. Only an
    // unrecognised letter falls back to considering every kind of marker.
    const ofType = (rows: ManeuverEventRow[], types: string[]) =>
      rows.filter((row) => types.includes(String(row.event_type ?? "").toUpperCase()));

    const candidates = ofType(allEvents, clickedType ? [clickedType] : MANEUVER_MARKER_EVENT_TYPES);

    if (candidates.length === 0) {
      warn(`${logPrefix}: No maneuver events to match against`, {
        datasetId,
        clickedType,
        eventsConsidered: allEvents.length
      });
      toastStore.showToast('warning', 'Could not select maneuver', 'No matching maneuver events in this dataset.');
      return null;
    }

    let matchingEvent: ManeuverEventRow | null = null;
    let closestEvent: ManeuverEventRow | null = null;
    let closestDistance = Infinity;

    for (const row of candidates) {
      const eventStart = new Date(row.start_time ?? "").getTime();
      const eventEnd = new Date(row.end_time ?? "").getTime();
      if (!Number.isFinite(eventStart) || !Number.isFinite(eventEnd)) continue;

      if (maneuverTime >= eventStart && maneuverTime <= eventEnd) {
        matchingEvent = row;
        break;
      }
      const minDist = Math.min(
        Math.abs(maneuverTime - eventStart),
        Math.abs(maneuverTime - eventEnd)
      );
      if (minDist < closestDistance) {
        closestDistance = minDist;
        closestEvent = row;
      }
    }

    // Only accept a near miss — beyond the tolerance the click is not on this maneuver at all.
    const eventToAdd = matchingEvent ?? (closestDistance <= MATCH_TOLERANCE_MS ? closestEvent : null);
    const eventId = Number(eventToAdd?.event_id);

    if (eventToAdd == null || !Number.isFinite(eventId)) {
      warn(`${logPrefix}: No event within tolerance for maneuver time`, {
        maneuverTime: new Date(maneuverTime).toISOString(),
        datasetId,
        clickedType,
        closestDistanceMs: Number.isFinite(closestDistance) ? closestDistance : null,
        toleranceMs: MATCH_TOLERANCE_MS,
        candidates: candidates.length
      });
      toastStore.showToast('warning', 'Could not select maneuver', 'No maneuver event matches this point.');
      return null;
    }

    debug(`${logPrefix}: Maneuver matched event`, {
      event_id: eventId,
      event_type: eventToAdd.event_type,
      wasExactMatch: !!matchingEvent,
      datasetId
    });

    // Trust the matched row's own type over the marker letter when they disagree.
    const eventType = String(eventToAdd.event_type ?? "").toUpperCase() || clickedType;
    return selectResolvedEvent(eventId, eventType || null);
  } catch (error) {
    warn(`${logPrefix}: Error resolving maneuver to event`, error);
    return null;
  }
}
